import React, { useEffect, useState } from 'react';
import { Checkbox } from '@/components/ui/checkbox';
import { Input } from '@/components/ui/input';
import { useLanguage } from '../contexts/LanguageContext';

/**
 * Working hours picker — 7 days with open/closed + start/end time pickers.
 *
 * Internally keeps an object keyed by ISO day abbreviation:
 *   { mon: { open: true, start: "09:00", end: "18:00" }, ... }
 *
 * Serializes to a human-readable string on change (via onChange prop):
 *   "P.-Pk. 09:00-18:00, S. 10:00-14:00, Sv. slēgts"
 *
 * Also parses the incoming string back to state so the picker works when
 * editing an existing shop.
 */
const DAYS = [
  { key: 'mon', lv: 'P.',  en: 'Mon' },
  { key: 'tue', lv: 'O.',  en: 'Tue' },
  { key: 'wed', lv: 'T.',  en: 'Wed' },
  { key: 'thu', lv: 'C.',  en: 'Thu' },
  { key: 'fri', lv: 'Pk.', en: 'Fri' },
  { key: 'sat', lv: 'S.',  en: 'Sat' },
  { key: 'sun', lv: 'Sv.', en: 'Sun' },
];

const DAY_FULL_LV = { mon: 'Pirmd.', tue: 'Otrd.', wed: 'Trešd.', thu: 'Ceturtd.', fri: 'Piektd.', sat: 'Sestd.', sun: 'Svētd.' };
const DAY_FULL_EN = { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' };

const DEFAULT_SCHEDULE = {
  mon: { open: true,  start: '09:00', end: '18:00' },
  tue: { open: true,  start: '09:00', end: '18:00' },
  wed: { open: true,  start: '09:00', end: '18:00' },
  thu: { open: true,  start: '09:00', end: '18:00' },
  fri: { open: true,  start: '09:00', end: '18:00' },
  sat: { open: false, start: '10:00', end: '16:00' },
  sun: { open: false, start: '10:00', end: '16:00' },
};

function serialize(schedule, language) {
  // Group consecutive identical days into ranges for compactness.
  const order = DAYS.map((d) => d.key);
  const parts = [];
  let i = 0;
  while (i < order.length) {
    const cur = schedule[order[i]] || { open: false };
    let j = i;
    while (
      j + 1 < order.length &&
      (schedule[order[j + 1]]?.open === cur.open) &&
      (schedule[order[j + 1]]?.start === cur.start) &&
      (schedule[order[j + 1]]?.end === cur.end)
    ) j++;

    const firstLabel = DAYS[i].lv;
    const lastLabel = DAYS[j].lv;
    const label = (i === j) ? firstLabel : `${firstLabel}-${lastLabel}`;
    if (!cur.open) {
      parts.push(`${label} ${language === 'lv' ? 'slēgts' : 'closed'}`);
    } else {
      parts.push(`${label} ${cur.start}-${cur.end}`);
    }
    i = j + 1;
  }
  return parts.join(', ');
}

// Best-effort parser: recognizes formats like "P.-Pk. 09:00-18:00, S. 10:00-16:00"
function parse(text) {
  const schedule = JSON.parse(JSON.stringify(DEFAULT_SCHEDULE));
  if (!text || typeof text !== 'string') return schedule;
  // Zero-out so we only fill what's parsed
  Object.keys(schedule).forEach((k) => { schedule[k] = { open: false, start: '09:00', end: '18:00' }; });

  const dayAlias = {
    'p': 'mon', 'pirmd': 'mon', 'pirmdien': 'mon', 'mon': 'mon',
    'o': 'tue', 'otr': 'tue', 'otrd': 'tue', 'tue': 'tue',
    't': 'wed', 'tresd': 'wed', 'trešd': 'wed', 'wed': 'wed',
    'c': 'thu', 'cet': 'thu', 'ceturtd': 'thu', 'thu': 'thu',
    'pk': 'fri', 'piektd': 'fri', 'fri': 'fri',
    's': 'sat', 'sest': 'sat', 'sestd': 'sat', 'sat': 'sat',
    'sv': 'sun', 'svet': 'sun', 'svētd': 'sun', 'sun': 'sun',
  };
  const order = DAYS.map((d) => d.key);

  for (const seg of text.split(/[,;]/).map(s => s.trim()).filter(Boolean)) {
    // Try "DAY[-DAY] TIME-TIME" or "DAY closed"
    const lower = seg.toLowerCase().replace(/\./g, '');
    const rangeMatch = lower.match(/^([a-zāēīōūčšžģķļņŗ]+)(?:\s*-\s*([a-zāēīōūčšžģķļņŗ]+))?\s+(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})$/);
    const closedMatch = lower.match(/^([a-zāēīōūčšžģķļņŗ]+)(?:\s*-\s*([a-zāēīōūčšžģķļņŗ]+))?\s+(sl[eē]gts|closed)$/);
    if (rangeMatch) {
      const d1 = dayAlias[rangeMatch[1]];
      const d2 = dayAlias[rangeMatch[2] || rangeMatch[1]];
      if (!d1 || !d2) continue;
      const i1 = order.indexOf(d1), i2 = order.indexOf(d2);
      for (let i = Math.min(i1, i2); i <= Math.max(i1, i2); i++) {
        schedule[order[i]] = { open: true, start: rangeMatch[3], end: rangeMatch[4] };
      }
    } else if (closedMatch) {
      const d1 = dayAlias[closedMatch[1]];
      const d2 = dayAlias[closedMatch[2] || closedMatch[1]];
      if (!d1 || !d2) continue;
      const i1 = order.indexOf(d1), i2 = order.indexOf(d2);
      for (let i = Math.min(i1, i2); i <= Math.max(i1, i2); i++) {
        schedule[order[i]] = { open: false, start: '09:00', end: '18:00' };
      }
    }
  }
  return schedule;
}

export default function WorkingHoursPicker({ value, onChange }) {
  const { language } = useLanguage();
  const [schedule, setSchedule] = useState(() => parse(value));

  // Re-sync when the incoming value changes from the outside (e.g., initial load)
  useEffect(() => {
    setSchedule(parse(value));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const updateDay = (day, patch) => {
    const next = { ...schedule, [day]: { ...schedule[day], ...patch } };
    setSchedule(next);
    onChange(serialize(next, language));
  };

  const dayFull = language === 'lv' ? DAY_FULL_LV : DAY_FULL_EN;

  return (
    <div className="space-y-2 rounded-xl border border-[#E5E4E0] bg-[#FAFAFA] p-3" data-testid="working-hours-picker">
      {DAYS.map((d) => {
        const row = schedule[d.key] || { open: false, start: '09:00', end: '18:00' };
        return (
          <div key={d.key} className="grid grid-cols-12 items-center gap-2">
            <div className="col-span-4 sm:col-span-3 flex items-center gap-2">
              <Checkbox
                id={`wh-${d.key}`}
                checked={!!row.open}
                onCheckedChange={(c) => updateDay(d.key, { open: !!c })}
                data-testid={`wh-${d.key}-open`}
              />
              <label htmlFor={`wh-${d.key}`} className="text-sm font-medium text-[#121212] cursor-pointer">
                {dayFull[d.key]}
              </label>
            </div>
            {row.open ? (
              <div className="col-span-8 sm:col-span-9 flex items-center gap-2">
                <Input
                  type="time"
                  value={row.start}
                  onChange={(e) => updateDay(d.key, { start: e.target.value })}
                  className="h-9 w-28 bg-white border-[#E5E4E0]"
                  data-testid={`wh-${d.key}-start`}
                />
                <span className="text-[#5C5C5C]">–</span>
                <Input
                  type="time"
                  value={row.end}
                  onChange={(e) => updateDay(d.key, { end: e.target.value })}
                  className="h-9 w-28 bg-white border-[#E5E4E0]"
                  data-testid={`wh-${d.key}-end`}
                />
              </div>
            ) : (
              <span className="col-span-8 sm:col-span-9 text-sm text-[#5C5C5C] italic">
                {language === 'lv' ? 'Slēgts' : 'Closed'}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
}
